import axios from "axios";
import { useEffect, useState } from "react";
import { FaPhoneAlt } from "react-icons/fa"; // Import phone icon for call button
import { MdOutlineBookmarkAdd } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../redux/authSlice";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./OutboundTemplate.module.css";

const OutboundTemplate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, userId, isAuthenticated } = useSelector((state) => state.auth);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [isPlaygroundOpen, setIsPlaygroundOpen] = useState(false); // State for popup
  const [selectedAgent, setSelectedAgent] = useState(""); // State for selected agent
  const [isCalling, setIsCalling] = useState(false); // State for call status

  useEffect(() => {
    if (!token || !userId || !isAuthenticated) {
      console.error("OutboundTemplate: Missing or invalid token/userID");
      dispatch(logout());
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(
          `https://prod.toxboxai.com/api/agent-creation/by-creator/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          console.warn("OutboundTemplate: Unauthorized, redirecting to login");
          dispatch(logout());
          navigate("/login");
          return;
        } else if (response.status === 404) {
          console.warn("OutboundTemplate: No agents found");
          setAgents([]);
        } else if (response.status !== 200) {
          throw new Error(
            `HTTP error: ${response.status} - ${response.statusText}`
          );
        }

        const result = response.data;
        console.log("OutboundTemplate: Fetched agents:", result.agents);
        const outboundAgents = result.agents.filter(
          (agent) => agent.type === "outbound"
        );
        setAgents(outboundAgents || []);
      } catch (error) {
        console.error("OutboundTemplate: Error fetching data:", error);
        setError(
          error.response?.data?.error ||
          "Failed to load data. Please try again later."
        );
        setAgents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, dispatch, token, userId, isAuthenticated]);

  const handleDelete = (agentId) => {
    const agent = agents.find((a) => a._id === agentId);
    if (!agent) {
      console.error("OutboundTemplate: Agent not found for deletion:", agentId);
      return;
    }
    setAgentToDelete(agent);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) {
      console.error("OutboundTemplate: No agent selected for deletion");
      return;
    }

    if (!token || !isAuthenticated) {
      alert("Authentication failed. Please log in again.");
      dispatch(logout());
      navigate("/login");
      return;
    }

    try {
      await axios.delete(
        `https://prod.toxboxai.com/api/agent-creation/delete/${agentToDelete._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setAgents((prevAgents) =>
        prevAgents.filter((agent) => agent._id !== agentToDelete._id)
      );
      console.log(
        "OutboundTemplate: Agent deleted successfully:",
        agentToDelete._id
      );
    } catch (error) {
      console.error("OutboundTemplate: Error deleting agent:", error);
      if (error.response?.status === 401) {
        alert("Session expired. Please log in again.");
        dispatch(logout());
        navigate("/login");
      } else {
        alert(
          error.response?.data?.error ||
          "Failed to delete agent. Please try again."
        );
      }
    } finally {
      setIsDeleteModalOpen(false);
      setAgentToDelete(null);
    }
  };

  const handleEditData = (agent) => {
    try {
      if (!agent?._id) {
        console.error("OutboundTemplate: Invalid agent ID:", agent);
        alert("Cannot edit agent: Invalid agent ID.");
        return;
      }
      console.log("OutboundTemplate: Navigating to edit agent:", agent);
      const agentData = {
        _id: agent._id,
        avatarName: agent.avatarName || "Unnamed Agent",
        phoneNumber: agent.phoneNumber || "",
        avatarImage: agent.avatarImage || "https://i.pravatar.cc/40",
        language: agent.language || "en-US",
        gender: agent.gender || "",
        voice: agent.voice?.displayName || agent.voice || "",
        templateId: agent.templateId || "",
        systemPrompt:
          typeof agent.systemPrompt === "object"
            ? agent.systemPrompt?.promptName || ""
            : agent.systemPrompt || "",
        model: agent.model || "fixie-ai/ultravox",
        temperature: agent.temperature || 0.3,
        firstSpeaking: agent.firstSpeaking || false,
        welcomeMessage: agent.firstSpeakerSettings?.agent?.welcomeMessage || "",
        maxDuration: agent.maxDuration
          ? agent.maxDuration.replace("s", "")
          : "",
        timeExceedMessage: agent.timeExceedMessage || "",
        transcript: agent.transcript !== undefined ? agent.transcript : false,
        callRecording:
          agent.callRecording !== undefined ? agent.callRecording : false,
        knowledgeBaseId: agent.knowledgeBaseId || "",
        type: agent.type || "outbound",
      };
      navigate(`/outbound/edit-agents/${agent._id}`, {
        state: { agent: agentData },
      });
    } catch (error) {
      console.error("OutboundTemplate: Error navigating to edit page:", error);
      alert("Failed to navigate to edit page.");
    }
  };

  const handleCreateAgent = () => {
    try {
      console.log("OutboundTemplate: Navigating to create agent");
      navigate("/outbound/createagent");
    } catch (error) {
      console.error(
        "OutboundTemplate: Error navigating to create agent:",
        error
      );
      alert("Failed to navigate to create agent page.");
    }
  };

  const handlePlaygroundOpen = () => {
    setIsPlaygroundOpen(true);
  };

  const handlePlaygroundClose = () => {
    setIsPlaygroundOpen(false);
    setSelectedAgent("");
    setIsCalling(false);
  };

  const handleAgentSelect = (e) => {
    setSelectedAgent(e.target.value);
  };

  const handleCallStart = () => {
    if (!selectedAgent) {
      alert("Please select an agent before starting a call.");
      return;
    }
    setIsCalling(true);
    // Simulate a call start (replace with actual call logic)
    console.log(`Starting call with agent: ${selectedAgent}`);
    // Example: Simulate call duration
    setTimeout(() => {
      setIsCalling(false);
      console.log("Call ended");
    }, 5000); // Simulate 5-second call
  };

  return (
    <div className={styles.container}>
      <div className={styles.textWrapper}>
        <p className={styles.heading1}>Build Your Smart Calling Assistant</p>
        {/* <p className={styles.subheading}>
          Ready to build your AI agent or update an existing one? Choose the
          voice, tone, and
          <br />
          personality that fits your brand. It’s quick, easy, and all about your
          needs!
        </p> */}
      </div>
      <h3 className={styles.heading2}>Outbound Template</h3>
      {/* <button
        onClick={handlePlaygroundOpen}
        className={styles.playgroundButton}
      >
        Open Playground
      </button> */}

      {isLoading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.grid}>
          <div className={styles.addNew} onClick={handleCreateAgent}>
            <span className={styles.plus}>+</span>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          {Array.isArray(agents) && agents.length > 0 ? (
            agents.map((agent, index) => {
              const topColorClass =
                index % 2 === 0 ? styles.greenBar : styles.blueBar;

              return (
                <div key={agent._id} className={styles.agentCard}>
                  <div className={`${styles.topBar} ${topColorClass}`}>
                    {/* <img
                      src={agent.avatarImage}
                      alt={agent.avatarName || "Unnamed Agent"}
                      className={styles.avatar}
                    /> */}
                  </div>
                  <h3 className={styles.agentName}>
                    {agent.avatarName || "Unnamed Agent"}
                  </h3>
                  <p className={styles.agentDescription}>
                    {agent.phoneNumber || "No Phone Number"}
                    <br />
                    Language: {agent.language || "Unknown"}
                    <br />
                    Voice:{" "}
                    {agent.voice?.displayName || agent.voice || "Unknown Voice"}
                    <br />
                    Template: {agent.templateId || "No Template Assigned"}
                    <br />
                    Prompt:{" "}
                    {typeof agent.systemPrompt === "object"
                      ? agent.systemPrompt?.promptName || "No Prompt"
                      : agent.systemPrompt || "No Prompt"}
                  </p>
                  <button
                    onClick={() => handleEditData(agent)}
                    className={styles.role}
                  >
                    Edit
                  </button>

                  <div className={styles.icons}>
                    <span
                      className={styles.icon}
                      onClick={() => handleDelete(agent._id)}
                      title="Delete Agent"
                    >
                      <RiDeleteBin6Line />
                    </span>
                    <span className={styles.icon} title="Bookmark Agent">
                      <MdOutlineBookmarkAdd />
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <p className={styles.subheading}>
              No outbound agents found. Create a new agent to get started!
            </p>
          )}
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAgentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        agentName={agentToDelete?.avatarName || "Unnamed Agent"}
      />

      {isPlaygroundOpen && (
        <div className={styles.playgroundModal}>
          <div className={styles.playgroundContent}>
            <button
              className={styles.closeButton}
              onClick={handlePlaygroundClose}
            >
              &times;
            </button>
            <h3 className={styles.playgroundTitle}>Call Playground</h3>
            <select
              value={selectedAgent}
              onChange={handleAgentSelect}
              className={styles.agentSelect}
              disabled={isCalling}
            >
              <option value="">Select an Agent</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.avatarName || "Unnamed Agent"}
                </option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Enter the number"
              className={styles.agentSelect}
            ></input>

            {isCalling && (
              <div className={styles.waveContainer}>
                <div className={styles.wave}></div>
                <div className={styles.wave}></div>
                <div className={styles.wave}></div>
              </div>
            )}
            <button
              onClick={handleCallStart}
              className={styles.callButton}
              disabled={isCalling || !selectedAgent}
            >
              <FaPhoneAlt /> Start Call
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboundTemplate;
